import {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import './AssociationOption.css';
import questionIcon from '../images/question.png';

const FEEDBACK_DURATION = 700;

const shuffleArray = (items) => {
  const shuffledItems = [...items];

  for (
    let currentIndex = shuffledItems.length - 1;
    currentIndex > 0;
    currentIndex -= 1
  ) {
    const randomIndex = Math.floor(
      Math.random() * (currentIndex + 1)
    );

    [
      shuffledItems[currentIndex],
      shuffledItems[randomIndex],
    ] = [
      shuffledItems[randomIndex],
      shuffledItems[currentIndex],
    ];
  }

  return shuffledItems;
};

const normalizeValue = (value) =>
  String(value ?? '')
    .trim()
    .toLowerCase();

const AssociationOption = ({
  question,
  answers = {},
  isAnswerChecked = false,
  onAnswersChange,
  onComplete,
}) => {
  const [selectedLeft, setSelectedLeft] =
    useState(null);

  const [selectedRight, setSelectedRight] =
    useState(null);

  const [
    temporaryFeedbacks,
    setTemporaryFeedbacks,
  ] = useState([]);

  const [
    localAnswers,
    setLocalAnswers,
  ] = useState({});

  const feedbackTimersRef = useRef([]);
  const feedbackIdRef = useRef(0);
  const answersRef = useRef({});
  const completedRef = useRef(false);

  const leftOptions = Array.isArray(
    question?.leftOptions
  )
    ? question.leftOptions
    : [];

  const rightOptions = Array.isArray(
    question?.rightOptions
  )
    ? question.rightOptions
    : [];

  const safeAnswers =
    answers &&
    typeof answers === 'object' &&
    !Array.isArray(answers)
      ? answers
      : {};

  const correctAnswer =
    question?.correctAnswer &&
    typeof question.correctAnswer === 'object' &&
    !Array.isArray(question.correctAnswer)
      ? question.correctAnswer
      : {};

  const questionKey = useMemo(
    () =>
      JSON.stringify({
        question: question?.question || '',
        leftOptions,
        rightOptions,
      }),
    [
      question?.question,
      question?.leftOptions,
      question?.rightOptions,
    ]
  );

  const randomizedLeftOptions = useMemo(
    () => shuffleArray(leftOptions),
    [questionKey]
  );

  const randomizedRightOptions = useMemo(
    () => shuffleArray(rightOptions),
    [questionKey]
  );

  const usedRightOptions = useMemo(
    () => Object.values(localAnswers),
    [localAnswers]
  );

  const clearFeedbackTimers = () => {
    feedbackTimersRef.current.forEach(
      (timer) => clearTimeout(timer)
    );

    feedbackTimersRef.current = [];
  };

  const resetSelections = () => {
    setSelectedLeft(null);
    setSelectedRight(null);
  };

  useEffect(() => {
    answersRef.current = safeAnswers;
    setLocalAnswers(safeAnswers);
  }, [answers]);

  useEffect(() => {
    clearFeedbackTimers();

    resetSelections();
    setTemporaryFeedbacks([]);

    answersRef.current = safeAnswers;
    setLocalAnswers(safeAnswers);

    completedRef.current = false;
  }, [questionKey]);

  useEffect(() => {
    return () => {
      clearFeedbackTimers();
    };
  }, []);

  const isLeftLocked = (leftOption) =>
    Object.prototype.hasOwnProperty.call(
      localAnswers,
      leftOption
    );

  const isRightLocked = (rightOption) =>
    usedRightOptions.some(
      (usedOption) =>
        normalizeValue(usedOption) ===
        normalizeValue(rightOption)
    );

  const addTemporaryFeedback = ({
    left,
    right,
    status,
    onFeedbackEnd,
  }) => {
    const feedbackId =
      feedbackIdRef.current + 1;

    feedbackIdRef.current = feedbackId;

    const newFeedback = {
      id: feedbackId,
      left,
      right,
      status,
    };

    setTemporaryFeedbacks(
      (currentFeedbacks) => [
        ...currentFeedbacks,
        newFeedback,
      ]
    );

    const timer = setTimeout(() => {
      setTemporaryFeedbacks(
        (currentFeedbacks) =>
          currentFeedbacks.filter(
            (feedback) =>
              feedback.id !== feedbackId
          )
      );

      feedbackTimersRef.current =
        feedbackTimersRef.current.filter(
          (currentTimer) =>
            currentTimer !== timer
        );

      onFeedbackEnd?.();
    }, FEEDBACK_DURATION);

    feedbackTimersRef.current.push(timer);
  };

  const getLatestLeftFeedback = (
    leftOption
  ) => {
    return [...temporaryFeedbacks]
      .reverse()
      .find(
        (feedback) =>
          feedback.left === leftOption
      );
  };

  const getLatestRightFeedback = (
    rightOption
  ) => {
    return [...temporaryFeedbacks]
      .reverse()
      .find(
        (feedback) =>
          feedback.right === rightOption
      );
  };

  const testPair = (
    leftOption,
    rightOption
  ) => {
    if (
      !leftOption ||
      !rightOption ||
      isAnswerChecked ||
      isLeftLocked(leftOption) ||
      isRightLocked(rightOption)
    ) {
      return;
    }

    const expectedRight =
      correctAnswer[leftOption];

    const isCorrect =
      normalizeValue(rightOption) ===
      normalizeValue(expectedRight);

    /*
     * Selectiile se reseteaza imediat.
     * Utilizatorul poate incepe urmatoarea
     * asociere in timpul feedback-ului.
     */
    resetSelections();

    if (isCorrect) {
      const nextAnswers = {
        ...answersRef.current,
        [leftOption]: rightOption,
      };

      answersRef.current = nextAnswers;
      setLocalAnswers(nextAnswers);

      onAnswersChange?.(nextAnswers);

      const allPairsCompleted =
        leftOptions.length > 0 &&
        Object.keys(nextAnswers).length ===
          leftOptions.length;

      addTemporaryFeedback({
        left: leftOption,
        right: rightOption,
        status: 'correct',
        onFeedbackEnd: () => {
          if (
            allPairsCompleted &&
            !completedRef.current
          ) {
            completedRef.current = true;
            onComplete?.(nextAnswers);
          }
        },
      });

      return;
    }

    addTemporaryFeedback({
      left: leftOption,
      right: rightOption,
      status: 'incorrect',
    });
  };

  const handleLeftClick = (
    leftOption
  ) => {
    if (
      isAnswerChecked ||
      isLeftLocked(leftOption)
    ) {
      return;
    }

    if (
      selectedLeft === leftOption
    ) {
      setSelectedLeft(null);
      return;
    }

    if (selectedRight) {
      testPair(
        leftOption,
        selectedRight
      );

      return;
    }

    setSelectedLeft(leftOption);
  };

  const handleRightClick = (
    rightOption
  ) => {
    if (
      isAnswerChecked ||
      isRightLocked(rightOption)
    ) {
      return;
    }

    if (
      selectedRight === rightOption
    ) {
      setSelectedRight(null);
      return;
    }

    if (selectedLeft) {
      testPair(
        selectedLeft,
        rightOption
      );

      return;
    }

    setSelectedRight(rightOption);
  };

  const getLeftClassName = (
    leftOption
  ) => {
    const classes = [
      'association-left-option-bc',
    ];

    const feedback =
      getLatestLeftFeedback(leftOption);

    if (feedback) {
      classes.push(
        feedback.status === 'correct'
          ? 'association-correct-feedback-bc'
          : 'association-incorrect-bc'
      );
    } else if (
      isLeftLocked(leftOption)
    ) {
      classes.push(
        'association-correct-bc',
        'association-option-locked-bc'
      );
    } else if (
      selectedLeft === leftOption
    ) {
      classes.push(
        'association-left-selected-bc'
      );
    }

    return classes.join(' ');
  };

  const getRightClassName = (
    rightOption
  ) => {
    const classes = [
      'association-right-option-bc',
    ];

    const feedback =
      getLatestRightFeedback(rightOption);

    if (feedback) {
      classes.push(
        feedback.status === 'correct'
          ? 'association-correct-feedback-bc'
          : 'association-incorrect-bc'
      );
    } else if (
      isRightLocked(rightOption)
    ) {
      classes.push(
        'association-correct-bc',
        'association-option-locked-bc'
      );
    } else if (
      selectedRight === rightOption
    ) {
      classes.push(
        'association-right-selected-bc'
      );
    }

    return classes.join(' ');
  };

  if (!question) {
    return null;
  }

  return (
    <div className="association-option-bc">
      {question.question && (
        <div className="association-question-bc">
          <img
            src={questionIcon}
            alt=""
            className="association-question-icon-bc"
            draggable={false}
          />

          <p>{question.question}</p>
        </div>
      )}

      <div className="association-columns-bc">
        <div className="association-column-bc">
          {randomizedLeftOptions.map(
            (leftOption, index) => {
              const locked =
                isLeftLocked(leftOption);

              return (
                <button
                  key={`${leftOption}-${index}`}
                  type="button"
                  className={getLeftClassName(
                    leftOption
                  )}
                  onClick={() =>
                    handleLeftClick(leftOption)
                  }
                  disabled={
                    isAnswerChecked ||
                    locked
                  }
                >
                  {leftOption}
                </button>
              );
            }
          )}
        </div>

        <div className="association-column-bc">
          {randomizedRightOptions.map(
            (rightOption, index) => {
              const locked =
                isRightLocked(rightOption);

              return (
                <button
                  key={`${rightOption}-${index}`}
                  type="button"
                  className={getRightClassName(
                    rightOption
                  )}
                  onClick={() =>
                    handleRightClick(rightOption)
                  }
                  disabled={
                    isAnswerChecked ||
                    locked
                  }
                >
                  {rightOption}
                </button>
              );
            }
          )}
        </div>
      </div>
    </div>
  );
};

export default AssociationOption;